
import { useState } from 'react';
import useScrollAnimation from '../../../hooks/useScrollAnimation';

const CalculadorHome = () => {
  const { ref: sectionRef, isVisible } = useScrollAnimation({ threshold: 0.1 });

  const [marca, setMarca] = useState('');
  const [modelo, setModelo] = useState('');
  const [año, setAño] = useState('');
  const [kilometraje, setKilometraje] = useState('');
  const [estado, setEstado] = useState('');
  const [combustible, setCombustible] = useState('');
  const [precioEstimado, setPrecioEstimado] = useState<{ min: number; max: number } | null>(null);
  const [calculando, setCalculando] = useState(false);

  const marcas = [
    'Audi', 'BMW', 'Citroën', 'Dacia', 'Fiat', 'Ford', 'Honda', 'Hyundai',
    'Kia', 'Mazda', 'Mercedes-Benz', 'Nissan', 'Opel', 'Peugeot', 'Renault',
    'Seat', 'Skoda', 'Toyota', 'Volkswagen', 'Volvo', 'Otra marca'
  ];

  const años = Array.from({ length: 30 }, (_, i) => (2025 - i).toString());

  const estados = [
    { value: 'funciona', label: 'Funciona correctamente', factor: 1.0 },
    { value: 'averia-leve', label: 'Avería leve', factor: 0.75 },
    { value: 'averia-grave', label: 'Avería grave / No arranca', factor: 0.5 },
    { value: 'accidentado-leve', label: 'Accidentado (daños leves)', factor: 0.6 },
    { value: 'accidentado-grave', label: 'Siniestro total', factor: 0.35 },
    { value: 'despiece', label: 'Solo para despiece', factor: 0.25 },
  ];

  const combustibles = [
    { value: 'gasolina', label: 'Gasolina', factor: 1.0 },
    { value: 'diesel', label: 'Diésel', factor: 1.1 },
    { value: 'hibrido', label: 'Híbrido', factor: 1.2 },
    { value: 'electrico', label: 'Eléctrico', factor: 1.3 },
    { value: 'gas', label: 'GLP / GNC', factor: 0.9 },
  ];

  const getKilometrajeLabel = (val: string) => {
    const map: Record<string, string> = {
      '25000': 'Menos de 50.000 km',
      '75000': '50.000 - 100.000 km',
      '125000': '100.000 - 150.000 km',
      '175000': '150.000 - 200.000 km',
      '225000': '200.000 - 250.000 km',
      '300000': 'Más de 250.000 km',
    };
    return map[val] || val;
  };

  const getEstadoLabel = (val: string) => {
    return estados.find(e => e.value === val)?.label || val;
  };

  const getCombustibleLabel = (val: string) => {
    return combustibles.find(c => c.value === val)?.label || val;
  };

  const calcularPrecio = () => {
    if (!marca || !año || !kilometraje || !estado || !combustible) return;

    setCalculando(true);

    setTimeout(() => {
      const añoNum = parseInt(año);
      const kmNum = parseInt(kilometraje);

      let precioBase = 800;

      const añosAntiguedad = 2025 - añoNum;
      if (añosAntiguedad <= 5) precioBase += 1500;
      else if (añosAntiguedad <= 10) precioBase += 800;
      else if (añosAntiguedad <= 15) precioBase += 400;
      else if (añosAntiguedad <= 20) precioBase += 200;

      if (kmNum < 50000) precioBase += 600;
      else if (kmNum < 100000) precioBase += 400;
      else if (kmNum < 150000) precioBase += 200;
      else if (kmNum < 200000) precioBase += 100;

      const marcasPremium = ['Audi', 'BMW', 'Mercedes-Benz', 'Volvo'];
      if (marcasPremium.includes(marca)) precioBase *= 1.4;

      const estadoFactor = estados.find(e => e.value === estado)?.factor || 0.5;
      precioBase *= estadoFactor;

      const combustibleFactor = combustibles.find(c => c.value === combustible)?.factor || 1.0;
      precioBase *= combustibleFactor;

      const min = Math.round(precioBase * 0.85 / 50) * 50;
      const max = Math.round(precioBase * 1.15 / 50) * 50;

      setPrecioEstimado({ min: Math.max(min, 100), max: Math.max(max, 200) });
      setCalculando(false);
    }, 1500);
  };

  const enviarWhatsApp = () => {
    if (!precioEstimado) return;

    const mensaje = `Hola, he calculado el precio estimado de mi coche en vuestra web y me gustaría recibir una valoración definitiva.

🚗 *Datos del vehículo:*
• Marca: ${marca}${modelo ? `\n• Modelo: ${modelo}` : ''}
• Año: ${año}
• Kilometraje: ${getKilometrajeLabel(kilometraje)}
• Estado: ${getEstadoLabel(estado)}
• Combustible: ${getCombustibleLabel(combustible)}

💰 *Precio estimado:* ${precioEstimado.min.toLocaleString('es-ES')}€ - ${precioEstimado.max.toLocaleString('es-ES')}€

¿Podrían darme una valoración más precisa? Gracias.`;

    const url = `https://wa.me/34932932913?text=${encodeURIComponent(mensaje)}`;
    window.open(url, '_blank');
  };

  const resetCalculadora = () => {
    setMarca('');
    setModelo('');
    setAño('');
    setKilometraje('');
    setEstado('');
    setCombustible('');
    setPrecioEstimado(null);
  };

  return (
    <section ref={sectionRef} id="calculador-precio" className="py-12 sm:py-20 bg-gradient-to-br from-slate-50 to-emerald-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div 
          className={`text-center mb-8 sm:mb-12 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-emerald-100 text-emerald-700 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
            <i className="ri-calculator-line mr-1 sm:mr-2"></i>
            Calculadora Online
          </span>
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-slate-800 mb-3 sm:mb-4">
            ¿Cuánto Vale tu Coche?
          </h2>
          <p className="text-sm sm:text-lg text-slate-600 max-w-2xl mx-auto px-4">
            Calcula el precio estimado de tu vehículo en segundos
          </p>
        </div>

        <div 
          className={`max-w-4xl mx-auto transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-emerald-600 px-4 sm:px-8 py-4 sm:py-5">
              <h3 className="text-base sm:text-xl font-semibold text-white flex items-center gap-2 sm:gap-3">
                <span className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-white/20 rounded-lg">
                  <i className="ri-car-line text-base sm:text-xl"></i>
                </span>
                Datos de tu Vehículo
              </h3>
            </div>

            <div className="p-4 sm:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                    Marca del vehículo *
                  </label>
                  <select
                    value={marca}
                    onChange={(e) => setMarca(e.target.value)}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all bg-white cursor-pointer"
                  >
                    <option value="">Selecciona una marca</option>
                    {marcas.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                    Modelo (opcional)
                  </label>
                  <input
                    type="text"
                    value={modelo}
                    onChange={(e) => setModelo(e.target.value)}
                    placeholder="Ej: Golf, Serie 3, León..."
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                    Año de matriculación *
                  </label>
                  <select
                    value={año}
                    onChange={(e) => setAño(e.target.value)}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all bg-white cursor-pointer"
                  >
                    <option value="">Selecciona el año</option>
                    {años.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                    Kilometraje *
                  </label>
                  <select
                    value={kilometraje}
                    onChange={(e) => setKilometraje(e.target.value)}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all bg-white cursor-pointer"
                  >
                    <option value="">Selecciona el kilometraje</option>
                    <option value="25000">Menos de 50.000 km</option>
                    <option value="75000">50.000 - 100.000 km</option>
                    <option value="125000">100.000 - 150.000 km</option>
                    <option value="175000">150.000 - 200.000 km</option>
                    <option value="225000">200.000 - 250.000 km</option>
                    <option value="300000">Más de 250.000 km</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                    Estado del vehículo *
                  </label>
                  <select
                    value={estado}
                    onChange={(e) => setEstado(e.target.value)}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all bg-white cursor-pointer"
                  >
                    <option value="">Selecciona el estado</option>
                    {estados.map((e) => (
                      <option key={e.value} value={e.value}>{e.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                    Tipo de combustible *
                  </label>
                  <select
                    value={combustible}
                    onChange={(e) => setCombustible(e.target.value)}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all bg-white cursor-pointer"
                  >
                    <option value="">Selecciona el combustible</option>
                    {combustibles.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-6 sm:mt-8 flex flex-col gap-3 sm:flex-row sm:gap-4">
                <button
                  onClick={calcularPrecio}
                  disabled={!marca || !año || !kilometraje || !estado || !combustible || calculando}
                  className="flex-1 px-6 sm:px-8 py-3 sm:py-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-all disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer text-sm sm:text-base"
                >
                  {calculando ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Calculando...
                    </>
                  ) : (
                    <>
                      <i className="ri-calculator-line"></i>
                      Calcular Precio
                    </>
                  )}
                </button>
                {precioEstimado && (
                  <button
                    onClick={resetCalculadora}
                    className="px-6 py-3 sm:py-4 border-2 border-slate-200 text-slate-600 font-medium rounded-lg hover:bg-slate-50 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer text-sm sm:text-base"
                  >
                    <i className="ri-refresh-line"></i>
                    Nueva consulta
                  </button>
                )}
              </div>

              {precioEstimado && (
                <div className="mt-6 sm:mt-8 p-4 sm:p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
                  <div className="text-center">
                    <p className="text-xs sm:text-sm text-emerald-700 font-medium mb-2">
                      Precio estimado para tu {marca} {modelo} ({año})
                    </p>
                    <div className="flex items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                      <span className="text-2xl sm:text-4xl md:text-5xl font-bold text-emerald-600">
                        {precioEstimado.min.toLocaleString('es-ES')}€
                      </span>
                      <span className="text-xl sm:text-2xl text-slate-400">-</span>
                      <span className="text-2xl sm:text-4xl md:text-5xl font-bold text-emerald-600">
                        {precioEstimado.max.toLocaleString('es-ES')}€
                      </span>
                    </div>
                    <p className="text-[10px] sm:text-sm text-slate-500 mb-4 sm:mb-6 px-2">
                      * Estimación orientativa. El precio final puede variar según la inspección.
                    </p>

                    <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
                      <button
                        onClick={enviarWhatsApp}
                        className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-green-500 text-white font-bold rounded-lg hover:bg-green-600 transition-all whitespace-nowrap cursor-pointer text-base sm:text-lg shadow-lg hover:shadow-xl"
                      >
                        <i className="ri-whatsapp-line text-lg sm:text-xl"></i>
                        Enviar por WhatsApp
                      </button>
                      <a
                        href="tel:932932913"
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 sm:py-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base"
                      >
                        <i className="ri-phone-line"></i>
                        Llamar: 932 932 913
                      </a>
                    </div>

                    <p className="text-[10px] sm:text-xs text-slate-400 mt-3 sm:mt-4 px-2">
                      Al pulsar "Enviar por WhatsApp" se abrirá WhatsApp con todos los datos
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 sm:mt-8 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
            {[
              { icon: 'ri-shield-check-line', label: 'Valoración Gratuita' },
              { icon: 'ri-time-line', label: 'Respuesta en 24h' },
              { icon: 'ri-money-euro-circle-line', label: 'Pago Inmediato' },
              { icon: 'ri-truck-line', label: 'Recogida Gratis' },
            ].map((badge, index) => (
              <div 
                key={index}
                className={`bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 text-center shadow-sm transition-all duration-500 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: isVisible ? `${400 + index * 100}ms` : '0ms' }}
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-2 sm:mb-3">
                  <i className={`${badge.icon} text-lg sm:text-xl text-emerald-600`}></i>
                </div>
                <p className="text-xs sm:text-sm font-medium text-slate-700">{badge.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CalculadorHome;
